"use client"

import React, { useId } from "react"

type Props = {
  look?: string | null
  color?: string | null
  color2?: string | null
  radius?: number
  className?: string
  style?: React.CSSProperties
}

/**
 * Swatch procedural de tile (SVG) — portado de la maqueta (prototipo-beta1.jsx).
 * Los productos no tienen fotos: la imagen se genera desde metadata.look/color/color2.
 */
export default function TileSwatch({
  look,
  color,
  color2,
  radius = 0,
  className,
  style,
}: Props) {
  const uid = useId().replace(/[^a-zA-Z0-9]/g, "")
  const gId = `g-${uid}`
  const glossId = `gloss-${uid}`
  const c1 = color || "#cfc4b2"
  const c2 = color2 || "#b8ab95"

  let body: React.ReactNode = null

  if (look === "subway") {
    body = (
      <g>
        <rect width="120" height="120" fill={c1} />
        {[0, 24, 48, 72, 96].map((y, r) => {
          const off = r % 2 ? -30 : 0
          return [0, 60, 120].map((x, i) => (
            <rect
              key={`${r}-${i}`}
              x={x + off}
              y={y}
              width="58"
              height="22"
              rx="2"
              fill={c2}
              stroke={c1}
              strokeWidth="2"
              opacity={0.55 + ((r + i) % 3) * 0.15}
            />
          ))
        })}
      </g>
    )
  } else if (look === "hexagon") {
    const hex = (cx: number, cy: number, s: number) =>
      Array.from({ length: 6 }, (_, k) => {
        const a = (Math.PI / 3) * k - Math.PI / 6
        return `${cx + s * Math.cos(a)},${cy + s * Math.sin(a)}`
      }).join(" ")
    body = (
      <g>
        <rect width="120" height="120" fill={c1} />
        {[0, 1, 2, 3].map((r) =>
          [0, 1, 2, 3].map((q) => {
            const cx = q * 32 + (r % 2 ? 16 : 0)
            const cy = r * 28 + 8
            return (
              <polygon
                key={`${r}-${q}`}
                points={hex(cx, cy, 17)}
                fill={c2}
                stroke={c1}
                strokeWidth="2.5"
                opacity={0.6 + ((r + q) % 3) * 0.13}
              />
            )
          })
        )}
      </g>
    )
  } else if (look === "marble") {
    body = (
      <g>
        <rect width="120" height="120" fill={c2} />
        <rect width="120" height="120" fill={`url(#${gId})`} opacity="0.5" />
        <path d="M-5 30 C 30 10, 60 55, 125 25" stroke={c1} strokeWidth="2.5" fill="none" opacity="0.7" />
        <path d="M-5 70 C 40 50, 70 95, 125 60" stroke={c1} strokeWidth="1.5" fill="none" opacity="0.5" />
        <path d="M20 -5 C 35 40, 10 70, 40 125" stroke={c1} strokeWidth="1" fill="none" opacity="0.45" />
        <path d="M85 -5 C 75 50, 100 80, 90 125" stroke={c1} strokeWidth="1.5" fill="none" opacity="0.5" />
      </g>
    )
  } else if (look === "terrazzo") {
    const chips = Array.from({ length: 26 }, (_, k) => {
      const x = (k * 47) % 120
      const y = (k * 71) % 120
      const palette = [c1, "#d8c7ad", "#c98b6b", "#8aa18a", "#e7dcc6"]
      return (
        <ellipse
          key={k}
          cx={x}
          cy={y}
          rx={3 + (k % 3) * 2}
          ry={2 + (k % 2) * 3}
          fill={palette[k % palette.length]}
          transform={`rotate(${(k * 33) % 180} ${x} ${y})`}
          opacity="0.85"
        />
      )
    })
    body = (
      <g>
        <rect width="120" height="120" fill={c2} />
        {chips}
      </g>
    )
  } else if (look === "penny") {
    body = (
      <g>
        <rect width="120" height="120" fill={c1} />
        {[0, 1, 2, 3, 4, 5].map((r) =>
          [0, 1, 2, 3, 4, 5].map((q) => (
            <circle
              key={`${r}-${q}`}
              cx={q * 22 + (r % 2 ? 11 : 0)}
              cy={r * 20 + 8}
              r="9"
              fill={c2}
              stroke={c1}
              strokeWidth="1.5"
              opacity={0.6 + ((r * q) % 3) * 0.13}
            />
          ))
        )}
      </g>
    )
  } else if (look === "herringbone") {
    body = (
      <g>
        <rect width="120" height="120" fill={c1} />
        {Array.from({ length: 7 }, (_, r) =>
          Array.from({ length: 7 }, (_, q) => {
            const x = q * 30 - 30
            const y = r * 30 - 30
            const rot = (q + r) % 2 ? 45 : -45
            return (
              <rect
                key={`${r}-${q}`}
                x={x}
                y={y}
                width="26"
                height="11"
                rx="1"
                fill={c2}
                stroke={c1}
                strokeWidth="1.5"
                transform={`rotate(${rot} ${x + 13} ${y + 5})`}
                opacity="0.7"
              />
            )
          })
        )}
      </g>
    )
  } else {
    // zellige / default — glossy irregular squares
    body = (
      <g>
        <rect width="120" height="120" fill={c1} />
        {[0, 1, 2, 3].map((r) =>
          [0, 1, 2, 3].map((q) => (
            <rect
              key={`${r}-${q}`}
              x={q * 30 + 3}
              y={r * 30 + 3}
              width="24"
              height="24"
              rx="2"
              fill={c2}
              stroke={c1}
              strokeWidth="2"
              opacity={0.55 + ((r + q) % 4) * 0.12}
            />
          ))
        )}
        <rect width="120" height="120" fill={`url(#${glossId})`} opacity="0.25" />
      </g>
    )
  }

  return (
    <svg
      viewBox="0 0 120 120"
      width="100%"
      height="100%"
      preserveAspectRatio="xMidYMid slice"
      className={className}
      style={{ borderRadius: radius, display: "block", ...style }}
    >
      <defs>
        <linearGradient id={gId} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#ffffff" stopOpacity="0.5" />
          <stop offset="1" stopColor="#000000" stopOpacity="0.08" />
        </linearGradient>
        <linearGradient id={glossId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#ffffff" />
          <stop offset="0.5" stopColor="#ffffff" stopOpacity="0" />
        </linearGradient>
      </defs>
      {body}
    </svg>
  )
}
